import { useEffect, useState } from "react";
import { gameApi } from "@/lib/api";
import { Utensils, ShoppingBag, Coins, PlusCircle, BookOpen, Home, Info, Lock, Sparkles } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const barnUrl = (p) => (!p ? null : p.startsWith("http") ? p : p.startsWith("/api/") ? `${BACKEND}${p}` : p);
const HINT_KEY = "bmm_game_barn_hint";

const NavBtn = ({ testid, icon: Icon, label, onClick }) => (
  <button data-testid={testid} onClick={onClick}
    className="flex flex-col items-center justify-center gap-1 rounded-xl border border-border bg-card py-2.5 text-[11px] font-bold text-primary active:scale-95">
    <Icon size={16} className="text-secondary" /> {label}
  </button>
);

export const BarnHome = ({ player, current, onOpenCurrent, onOpenStall, onOpenTack, onOpenFeed, onNewMakeover, onCollect, onAlumni, onGameHome, onUpgrades }) => {
  const [cfg, setCfg] = useState(null);
  const [hint, setHint] = useState(false);

  useEffect(() => { gameApi.get("/game/stable/config").then((r) => setCfg(r.data)).catch(() => setCfg(null)); }, [player]);
  useEffect(() => { try { setHint(!localStorage.getItem(HINT_KEY)); } catch { /* ignore */ } }, []);
  const dismissHint = () => { try { localStorage.setItem(HINT_KEY, "1"); } catch { /* ignore */ } setHint(false); };

  const stallByNum = {};
  (cfg?.stalls || []).forEach((s) => { stallByNum[s.number] = s; });

  // Place the active makeover horse into the first empty owned stall.
  if (current) {
    const empty = (cfg?.stalls || []).find((s) => s.state === "empty");
    if (empty) {
      stallByNum[empty.number] = { ...empty, state: "occupied", horse: { name: current.name, is_current: true } };
    }
  }

  const handleHotspot = (h) => {
    if (h.type === "feedroom") return onOpenFeed && onOpenFeed();
    if (h.type === "tackroom") return onOpenTack && onOpenTack();
    if (h.type === "stall") {
      const s = stallByNum[h.stall_number];
      if (s?.horse?.is_current) return onOpenCurrent && onOpenCurrent();
      if (s) onOpenStall(s);
    }
  };

  return (
    <div className="mb-4" data-testid="barn-home">
      {hint && (
        <div data-testid="barn-hint" className="mb-2 flex items-center gap-2 rounded-xl border border-secondary/40 bg-secondary/10 px-3 py-2 text-xs font-semibold text-secondary">
          <Info size={14} /> Tap a door or stall to explore your stable.
          <button data-testid="barn-hint-dismiss" onClick={dismissHint} className="ml-auto rounded-full px-2 py-0.5 text-[11px] font-bold text-secondary/80 hover:bg-secondary/15">Got it</button>
        </div>
      )}
      <div className="mb-1 text-center">
        <span data-testid="barn-stable-name" className="font-display text-base font-black text-primary">🪧 {cfg?.stable_name || `${player?.display_name || "My"}'s Stable`}</span>
      </div>

      <div className="relative w-full overflow-hidden rounded-2xl border-2 border-primary/20 shadow-lg" data-testid="barn-image-wrap">
        {cfg ? (
          <img data-testid="barn-image" src={barnUrl(cfg.image_path)} alt="Your stable barn interior" className="block h-auto w-full select-none" draggable={false} />
        ) : (
          <div className="grid aspect-[4/3] w-full place-items-center bg-muted text-sm text-muted-foreground">Loading your stable…</div>
        )}
        {cfg && (cfg.hotspots || []).map((h) => {
          const s = h.type === "stall" ? stallByNum[h.stall_number] : null;
          const occupied = s?.state === "occupied";
          const unavailable = s?.state === "unavailable";
          const label = h.type === "stall" && s ? (occupied ? s.horse?.name : unavailable ? "Unavailable" : `Stall ${h.stall_number}`) : h.label;
          const aria = h.type === "stall"
            ? `Stall ${h.stall_number}${occupied ? `, ${s.horse?.name}` : unavailable ? ", unavailable" : ", empty"}`
            : h.label;
          return (
            <button key={h.id} data-testid={`hotspot-${h.id}`} aria-label={aria} title={label}
              onClick={() => handleHotspot(h)}
              className="group absolute rounded-md ring-2 ring-transparent transition-all hover:bg-secondary/10 hover:ring-secondary focus:outline-none focus:ring-secondary active:scale-[0.98]"
              style={{ left: `${h.x}%`, top: `${h.y}%`, width: `${h.w}%`, height: `${h.h}%` }}>
              {h.type === "stall" && s && (unavailable ? (
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <Lock size={13} className="text-white/85 drop-shadow" data-testid={`hotspot-${h.id}-lock`} />
                </span>
              ) : (
                <span data-testid={`hotspot-${h.id}-nameplate`}
                  className={`pointer-events-none absolute inset-x-1 bottom-1 mx-auto max-w-[96%] truncate rounded border px-1.5 py-0.5 text-center text-[10px] font-black uppercase tracking-wide shadow ${
                    occupied
                      ? (s.horse?.is_current ? "border-secondary/70 bg-secondary text-white" : s.horse?.is_favorite ? "border-amber-300 bg-amber-500 text-white" : "border-amber-950/50 bg-amber-900/90 text-amber-50")
                      : "border-amber-950/40 bg-amber-800/75 text-amber-50"}`}>
                  {occupied ? s.horse?.name : "Empty Stall"}
                </span>
              ))}
              {h.type !== "stall" && (
                <span className="pointer-events-none absolute inset-x-0 bottom-0 mx-auto max-w-full translate-y-1 truncate rounded bg-black/65 px-1 py-[1px] text-center text-[9px] font-bold text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100">{label}</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-2 grid grid-cols-3 gap-2" data-testid="barn-nav">
        <NavBtn testid="barn-nav-feed" icon={Utensils} label="Feed Room" onClick={onOpenFeed} />
        <NavBtn testid="barn-nav-tack" icon={ShoppingBag} label="Tack Room" onClick={onOpenTack} />
        <NavBtn testid="barn-nav-collect" icon={Coins} label="Collect" onClick={onCollect} />
        <NavBtn testid="barn-nav-newmakeover" icon={PlusCircle} label="New Makeover" onClick={onNewMakeover} />
        <NavBtn testid="barn-nav-alumni" icon={BookOpen} label="Alumni Book" onClick={onAlumni} />
        <NavBtn testid="barn-nav-upgrades" icon={Sparkles} label="Upgrades" onClick={onUpgrades} />
        <NavBtn testid="barn-nav-gamehome" icon={Home} label="Game Home" onClick={onGameHome} />
      </div>
    </div>
  );
};
